/**
 * RFC 5322 address-header helpers for the agent EMAIL tab.
 *
 * The list column shows ONE participant per row, and which one depends on the
 * mailbox: in a received folder the SENDER is the correspondent, in a sent
 * folder the sender is always the agent itself (useless — every row would read
 * the same) and the RECIPIENT is what identifies the message. Outlook does
 * exactly this, and so does every mail client worth copying.
 *
 * Naming follows the operator's rule: show a HUMAN NAME when one is known,
 * fall back to the raw address only when it is not. Two sources of a name, in
 * order of trust:
 *   1. the Lexiun CONTACTS store, matched on the address — authoritative,
 *      curated, and the same name the lawyer sees everywhere else in LexIDE;
 *   2. the header's own display-name — self-asserted by the sender, so it is a
 *      fallback rather than a source of truth (`mail.list` frequently returns a
 *      bare address with no display name at all, which is precisely why 1
 *      matters).
 */

/** One parsed address from an address header. */
export interface ParsedAddress {
  /** Header display-name, unquoted. Absent when the header carried none. */
  name?: string;
  /** The addr-spec (`user@host`), lowercased for matching. Absent if unparseable. */
  address?: string;
  /** The original text, so an unparseable header is never silently dropped. */
  raw: string;
}

/**
 * Splits an address header on commas that are NOT inside quotes or angle
 * brackets — a display name is allowed to contain a comma ("Silva, João"
 * <j@x>), so a naive `split(",")` tears one correspondent into two.
 */
function splitAddressList(raw: string): string[] {
  const out: string[] = [];
  let current = "";
  let inQuotes = false;
  let inAngle = false;
  for (let i = 0; i < raw.length; i++) {
    const ch = raw[i]!;
    if (ch === '"' && raw[i - 1] !== "\\") inQuotes = !inQuotes;
    else if (ch === "<" && !inQuotes) inAngle = true;
    else if (ch === ">" && !inQuotes) inAngle = false;
    if (ch === "," && !inQuotes && !inAngle) {
      out.push(current);
      current = "";
      continue;
    }
    current += ch;
  }
  out.push(current);
  return out.map((s) => s.trim()).filter(Boolean);
}

/** Parses one address: `Name <a@b>` · `"Name" <a@b>` · `<a@b>` · `a@b`. */
export function parseAddress(raw: string): ParsedAddress {
  const trimmed = raw.trim();
  const angle = /^(.*?)<([^>]*)>\s*$/s.exec(trimmed);
  if (angle) {
    const name = angle[1]!.trim().replace(/^"(.*)"$/s, "$1").trim();
    const address = angle[2]!.trim().toLowerCase();
    return { name: name || undefined, address: address || undefined, raw: trimmed };
  }
  // No angle brackets: the whole thing is an address if it looks like one.
  if (/^[^\s@]+@[^\s@]+$/.test(trimmed)) {
    return { address: trimmed.toLowerCase(), raw: trimmed };
  }
  return { raw: trimmed };
}

/** Parses a full address header into its individual addresses. */
export function parseAddressList(raw: string | undefined): ParsedAddress[] {
  if (!raw || !raw.trim()) return [];
  return splitAddressList(raw).map(parseAddress);
}

/** What the list row should print for a participant. */
export interface ParticipantLabel {
  /** The name (or address) to show. Empty when the header was empty. */
  label: string;
  /** Additional recipients beyond the first, for a "+N" affordance. */
  extraCount: number;
  /** True when `label` is a bare address — no name was found anywhere. */
  isAddress: boolean;
}

/**
 * Resolves the one participant a list row should show.
 *
 * `resolveContactName` maps a lowercased address to a curated contact name;
 * pass `undefined` (or a function returning undefined) to fall back to header
 * display-names alone. The lookup wins over the header on purpose — a contact
 * the lawyer named "Dr. Mário S. Júnior" should not render as whatever string
 * the sender chose to put in their own From header.
 */
export function participantLabel(
  raw: string | undefined,
  resolveContactName?: (address: string) => string | undefined,
): ParticipantLabel {
  const list = parseAddressList(raw);
  if (list.length === 0) return { label: "", extraCount: 0, isAddress: false };

  const first = list[0]!;
  const contactName = first.address ? resolveContactName?.(first.address) : undefined;
  const label = contactName || first.name || first.address || first.raw;
  return {
    label,
    extraCount: list.length - 1,
    isAddress: !contactName && !first.name,
  };
}

/**
 * Builds an address → display-name index from the contacts store.
 *
 * Addresses are lowercased on both sides so matching is case-insensitive, which
 * RFC 5321 requires for the domain and every real mail system does for the
 * local part too. A contact with no name contributes nothing rather than
 * masking the address with an empty string.
 */
export function buildContactNameIndex(
  contacts: { displayName?: string; emails?: { address: string }[] }[],
): Map<string, string> {
  const index = new Map<string, string>();
  for (const c of contacts) {
    const name = c.displayName?.trim();
    if (!name) continue;
    for (const e of c.emails ?? []) {
      const addr = e.address?.trim().toLowerCase();
      // First writer wins: contacts are returned in a stable order, so a
      // duplicated address resolves deterministically instead of by luck.
      if (addr && !index.has(addr)) index.set(addr, name);
    }
  }
  return index;
}

/**
 * Does this mailbox identify its messages by RECIPIENT rather than sender?
 *
 * Matched on the canonical id first, then on the localized name, because the
 * runtime returns ids (`inbox`/`sent`/`spam`) while a future/custom mailbox may
 * only be recognisable by its label.
 */
export function mailboxShowsRecipient(mailboxId: string, mailboxName?: string): boolean {
  const hay = `${mailboxId} ${mailboxName ?? ""}`.toLowerCase();
  return /\b(sent|enviad|outbox|saida|saída|draft|rascunho|borrador)/.test(hay);
}
